import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaTimes } from "react-icons/fa";

import { useUIStateStore } from "@/store/uiStateStore";

import { useGetMySongsDetailed } from "../../../../api/queries";
import Table from "../../../../components/Table";
import useGetColours from "../../../../helpers/getColours";
import { strToBool } from "../../../../helpers/helpers";
// import AppState from "../../../../store/appstate";
import useAppStore from "../../../../store/appStateZustand";

function SplitCell({ cell }) {
  return (
    <div>
      {cell.row.values.split}
      %
    </div>
  );
}

function RightToCollectCell({ cell, isTunecore, statusSuccessColour, t }) {
  return (
    <div>
      {strToBool(cell.row.values.righttocollect) ? (
        <span style={{ color: isTunecore ? "#00ef86" : statusSuccessColour }}>
          <FaCheck className="inline mr-2" />
          {t("other:right-to-collect")}
        </span>
      ) : (
        <span className={`${isTunecore ? "text-tunecore-error-red" : "text-red-600"}`}>
          <FaTimes className="inline mr-2" />
          {t("other:no-right-to-collect")}
        </span>
      )}
    </div>
  );
}

function RightToCollectCellWrapper(props) {
  const { cell, isTunecore, statusSuccessColour, t } = props;
  return (
    <RightToCollectCell
      cell={cell}
      isTunecore={isTunecore}
      statusSuccessColour={statusSuccessColour}
      t={t}
    />
  );
}

function WritersSongsTable({ highlightColour, writercode }) {
  const { t } = useTranslation(["writers", "songs", "other"]);
  // const selectedAccount = AppState.useState(s => s.selectedAccount);
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const [data, setData] = useState([]);
  const songs = useGetMySongsDetailed({ aac: selectedAccount, wc: writercode });

  const { statusSuccessColour } = useGetColours();
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const designationCodes = useMemo(() => [
    { name: t("songs:author-option"), value: 1 },
    { name: t("songs:composer-option"), value: 2 },
    { name: t("songs:both-option"), value: 3 },
  ], [t]);

  useEffect(() => {
    if (songs.isSuccess && songs.data?.works) {
      setData(
        songs.data.works.map((w) => {
          const writerSplit = w.writersplits?.find(s => s.writercode === writercode);

          return {
            title: w.title,
            split: writerSplit?.writershare ?? 0,
            righttocollect: writerSplit?.righttocollect ?? false,
            designation: designationCodes.find(d => d.value === writerSplit?.writerdesignationcode)?.name ?? "",
          };
        }),
      );
    }
  }, [songs.isSuccess, songs.data, writercode, designationCodes]);

  const columns = useMemo(
    () => [
      {
        Header: t("writers:_title"),
        accessor: "title",
        width: "25%",
      },
      {
        Header: t("writers:writer-split"),
        accessor: "split",
        width: "25%",
        Cell: SplitCell,
      },
      {
        Header: t("writers:writer-controlled"),
        accessor: "righttocollect",
        width: "25%",
        Cell: props => RightToCollectCellWrapper({
          ...props,
          isTunecore,
          statusSuccessColour,
          t,
        }),
      },
      {
        Header: t("writers:designation-code"),
        accessor: "designation",
        width: "25%",
      },
    ],
    [t, isTunecore, statusSuccessColour],
  );

  const noneMessage = songs.isLoading || songs.isFetching
    ? `${t("writers:loading-writers-songs")}...`
    : songs.isError
      ? t("writers:error-loading-writers-songs")
      : t("writers:no-songs-available-for-writer");

  return (
    <Table
      data={data}
      columns={columns}
      highlightColour={highlightColour}
      revealComponent={null}
      noneMessage={noneMessage}
      searchSuffix={t("common:songs")}
      disableSearch
      title={t("writers:songs-written-header")}
      bgClass={`${isTunecore ? "bg-tunecore-black" : "bg-white"} rounded-lg p-0`}
    />
  );
}

export default WritersSongsTable;
